import { createTheme, type PaletteMode, alpha } from "@mui/material";

export function buildTheme(mode: PaletteMode) {
  const isLight = mode === "light";

  const primary = isLight
    ? { main: "#78909c", light: "#a7c0cd", dark: "#4b636e" }
    : { main: "#b0bec5", light: "#e2f1f8", dark: "#808e95" };

  return createTheme({
    palette: {
      mode,
      primary,
      secondary: { main: isLight ? "#90a4ae" : "#78909c" },
      ...(isLight
        ? { background: { default: "#fafafa", paper: "#ffffff" } }
        : { background: { default: "#121212", paper: "#1e1e1e" } }),
    },
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    },
    shape: { borderRadius: 10 },
    components: {
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: ({ theme }) => ({
            textTransform: "none" as const,
            fontWeight: 600,
            borderRadius: 8,
            transition: "all 0.2s ease",
            "&:hover": {
              transform: "translateY(-1px)",
              boxShadow: theme.shadows[4],
            },
            "&:active": {
              transform: "translateY(0)",
              boxShadow: theme.shadows[1],
            },
          }),
          contained: ({ theme }) => ({
            "&:hover": {
              backgroundColor: theme.palette.primary.dark,
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.35)}`,
            },
          }),
          outlined: ({ theme }) => ({
            "&:hover": {
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
              borderColor: theme.palette.primary.main,
            },
          }),
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: alpha(theme.palette.primary.main, 0.12),
              transform: "scale(1.1)",
            },
          }),
        },
      },
      MuiCard: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: 12,
            transition: "box-shadow 0.2s ease, transform 0.2s ease",
            "&:hover": {
              boxShadow: theme.shadows[6],
            },
          }),
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: 8,
            margin: "2px 8px",
            transition: "all 0.15s ease",
            "&:hover": {
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
            },
            "&.Mui-selected": {
              backgroundColor: alpha(theme.palette.primary.main, 0.15),
              "&:hover": {
                backgroundColor: alpha(theme.palette.primary.main, 0.22),
              },
              "& .MuiListItemIcon-root": {
                color: theme.palette.primary.main,
              },
              "& .MuiListItemText-primary": {
                fontWeight: 600,
                color: theme.palette.primary.main,
              },
            },
          }),
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              borderRadius: 8,
            },
          },
        },
      },
    },
  });
}
